// Phase T2.0A Prompt 2.2 — Member-facing Payment section.
//
// Entry fee is always displayed as PER PLAYER (universal club rule).
// Renders three routes based on `event.payment_type`:
//   • stripe → Embedded Stripe Checkout in-page for the current viewer.
//              Per-player payment. Registration precedes payment.
//              Webhook is authoritative; self-heal poll is the safety net.
//   • cash/venmo/zelle → static informational card. External Pay button
//              only when a `payment_link` exists and method != cash.
//   • unset → nothing.

import React from 'react';
import { ArrowSquareOut, CurrencyDollar, Check } from '@phosphor-icons/react';
import StripeEmbeddedPay from './StripeEmbeddedPay';

const METHOD_LABEL = {
  zelle:  'Zelle',
  venmo:  'Venmo',
  cash:   'Cash',
  stripe: 'Stripe',
};

function formatFee(cents) {
  if (cents == null) return null;
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

/**
 * @param {object}   props
 * @param {object}   props.event           Tournament / Round Robin row.
 * @param {'tournament'|'round_robin'} props.eventKind
 * @param {object=}  props.viewer          Signed-in user (null if anon).
 * @param {boolean=} props.viewerRegistered  Caller is a status='registered'
 *                                           participant (not an alternate).
 * @param {boolean=} props.viewerPaid       Caller has been marked paid.
 * @param {()=>void=} props.onPaid          Called after payment lands (for reload).
 * @param {number=}  props.autoStartToken   Bumped by the shared post-registration
 *                                          payment prompt to auto-open Stripe.
 */
export default function PaymentSection({
  event, eventKind = 'tournament',
  viewer, viewerRegistered = false, viewerPaid = false, onPaid,
  autoStartToken = 0,
}) {
  const fee = formatFee(event.entry_fee_cents);
  const method = event.payment_type ? METHOD_LABEL[event.payment_type] : null;
  const link = event.payment_link || null;
  const isStripe = event.payment_type === 'stripe';
  const isCash   = event.payment_type === 'cash';

  // Nothing configured at all → render nothing so legacy events stay clean.
  if (!fee && !method && !link) return null;

  const showExternalPayButton = !!link && !isCash && !isStripe;

  return (
    <div id="event-payment-section" className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-3" data-testid="payment-section">
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Payment</div>

      {fee && (
        <div data-testid="payment-fee">
          <div className="text-[10px] uppercase tracking-wider text-[#9aa39d]">Entry Fee</div>
          <div className="text-[16px] text-[#013523] font-semibold flex items-center gap-1.5">
            <CurrencyDollar size={16} weight="duotone" className="text-[#c9a93a]" />
            {fee === 'Free' ? 'Free' : `${fee} per player`}
          </div>
        </div>
      )}

      {method && (
        <div data-testid="payment-method">
          <div className="text-[10px] uppercase tracking-wider text-[#9aa39d]">Payment Method</div>
          <div className="text-[14px] text-[#013523] font-medium">{method}</div>
        </div>
      )}

      {/* Stripe path — per-viewer state. */}
      {isStripe && viewerPaid && (
        <div
          data-testid="payment-stripe-paid"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold text-[#015e3f] bg-[#e6f2ec] border border-[#015e3f]/30"
        >
          <Check size={12} weight="bold" /> Paid
        </div>
      )}
      {isStripe && !viewerPaid && (
        <StripeEmbeddedPay
          eventKind={eventKind}
          eventId={event.id}
          viewer={viewer}
          viewerCanPay={!!viewerRegistered}
          notEligibleReason={
            viewer && !viewerRegistered
              ? 'Register for this event first, then you can pay.'
              : undefined
          }
          ctaLabel="Pay Now"
          onPaid={onPaid}
          testIdPrefix="payment-stripe"
          autoStartToken={autoStartToken}
        />
      )}

      {/* External-link path — venmo / zelle only. Cash never shows a link. */}
      {showExternalPayButton && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="payment-pay-btn"
          className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto sm:min-w-[220px] px-4 py-2.5 rounded-full bg-[#013523] text-white text-[13px] font-semibold hover:bg-[#024029] transition-colors min-h-[44px]"
        >
          Pay for Event <ArrowSquareOut size={14} weight="bold" />
        </a>
      )}
    </div>
  );
}
